import l10n from '../../../shared/strings';

const MEDIA_ERROR_SUPPRESS_MS = 8000;
let suppressMediaErrorToastsUntil = 0;

/**
 * Briefly skip sticky media-load error toasts after a channel gone notice so they
 * cannot re-queue on top of (or ahead of) the Gone toast.
 */
export const suppressMediaErrorToastsBriefly = (ms = MEDIA_ERROR_SUPPRESS_MS) => {
  suppressMediaErrorToastsUntil = Date.now() + ms;
};

export const shouldSuppressMediaErrorToast = () => (
  Date.now() < suppressMediaErrorToastsUntil
);

const showNeutralChannelGoneToast = ({ Toast, message } = {}) => {
  if (typeof Toast !== 'function') return;

  if (typeof Toast.dismissAll === 'function') {
    Toast.dismissAll();
  }

  suppressMediaErrorToastsBriefly();

  Toast(message, {
    autoClose: 5000,
    closeButton: true,
  });
};

/**
 * Neutral toast when sync-on-read finds the linked Shopify product was deleted
 * and the listing was reverted to local draft.
 */
export const showChannelProductGoneToast = ({ Toast } = {}) => {
  showNeutralChannelGoneToast({
    Toast,
    message: l10n.TOAST.SHOPIFY_PRODUCT_DELETED_SHOWING_DRAFT,
  });
};

/**
 * Neutral toast when sync-on-read finds a linked Shopify variant was deleted
 * and the listing variant was reverted to local draft.
 */
export const showChannelVariantGoneToast = ({ Toast } = {}) => {
  showNeutralChannelGoneToast({
    Toast,
    message: l10n.TOAST.SHOPIFY_VARIANT_DELETED_SHOWING_DRAFT,
  });
};

export const hasChannelProductGoneFlag = (items = []) => (
  (items || []).some((item) => Boolean(item?.channelProductGone))
);

export const hasChannelVariantGoneFlag = (items = []) => (
  (items || []).some((item) => Boolean(item?.channelVariantGone))
);

/**
 * Prefer product-gone toast; otherwise show variant-gone when flagged.
 */
export const showChannelGoneToastFromItems = ({ Toast, items } = {}) => {
  if (hasChannelProductGoneFlag(items)) {
    showChannelProductGoneToast({ Toast });
    return 'product';
  }
  if (hasChannelVariantGoneFlag(items)) {
    showChannelVariantGoneToast({ Toast });
    return 'variant';
  }
  return null;
};

const hasLiveStoreId = (item) => Boolean(item?.live?.storeId);

const indexRowId = (row) => String(row?.listingVariantId || row?._id || '');

/**
 * When the card catch-up has no ephemeral flags (other API instance), compare
 * the previous on-screen live snapshot with the new Mongo payload.
 */
export const showChannelGoneToastsFromRefresh = ({
  Toast,
  previousListings = [],
  nextListings = [],
  previousIndex = [],
  nextIndex = [],
} = {}) => {
  const previousListingById = new Map(
    (previousListings || []).map((listing) => [String(listing._id), listing]),
  );
  const productGone = (nextListings || []).some((listing) => {
    const previous = previousListingById.get(String(listing._id));
    return hasLiveStoreId(previous) && !hasLiveStoreId(listing);
  });
  if (productGone) {
    showChannelProductGoneToast({ Toast });
    return 'product';
  }

  const previousIndexById = new Map(
    (previousIndex || []).map((row) => [indexRowId(row), row]),
  );
  const variantGone = (nextIndex || []).some((row) => {
    const previous = previousIndexById.get(indexRowId(row));
    return hasLiveStoreId(previous) && !hasLiveStoreId(row);
  });
  if (variantGone) {
    showChannelVariantGoneToast({ Toast });
    return 'variant';
  }

  return null;
};
